import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, FileText, ExternalLink, AlertTriangle, Users2 } from "lucide-react";
import api from "../lib/api";

const STATUS_FR = {
  active: "Actif", trialing: "Essai", past_due: "Paiement en retard",
  canceled: "Résilié", unpaid: "Impayé", incomplete: "Incomplet",
};

export default function Billing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/billing/me");
      setData(res.data);
    } catch (e) {
      toast.error("Impossible de charger la facturation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cancel = async () => {
    setCanceling(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Abonnement programmé pour résiliation en fin de période");
      setConfirm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la résiliation");
    } finally {
      setCanceling(false);
    }
  };

  const fmtDate = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString("fr-FR") : "—");

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portail de paiement indisponible");
    }
  };

  if (loading)
    return (
      <div className="ml-64 min-h-screen flex items-center justify-center" data-testid="billing-loading">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );

  const sub = data?.subscription;
  const hasSub = !!sub;

  return (
    <div className="ml-64 min-h-screen bg-slate-50 p-8" data-testid="billing-page">
      <div className="max-w-4xl">
        <h1 className="font-heading font-black text-4xl mb-1">Facturation</h1>
        <p className="text-slate-500 font-medium mb-8">Votre abonnement, vos factures et la résiliation.</p>

        {/* Plan card */}
        <div className="nb-card p-6 mb-6" data-testid="billing-plan-card">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 text-indigo-600 mb-2">
                <CreditCard size={20} strokeWidth={2.5} />
                <span className="text-xs uppercase tracking-widest font-bold">Formule actuelle</span>
              </div>
              <p className="font-heading font-black text-2xl" data-testid="billing-plan-label">
                {data?.plan_label || "Aucune formule associée"}
              </p>
              <div className="flex items-center gap-2 text-slate-600 font-semibold mt-2 text-sm">
                <Users2 size={16} />
                {data?.seats_used ?? 0}
                {data?.seat_limit ? ` / ${data.seat_limit === 999 ? "∞" : data.seat_limit}` : ""} utilisateur(s)
              </div>
            </div>
            {hasSub && (
              <span
                data-testid="billing-status"
                className={`px-3 py-1.5 rounded-lg border-2 border-slate-900 font-bold text-sm ${
                  sub.status === "active" && !sub.cancel_at_period_end
                    ? "bg-emerald-300"
                    : sub.cancel_at_period_end
                    ? "bg-amber-300"
                    : "bg-red-300"
                }`}
              >
                {sub.cancel_at_period_end ? "Résiliation programmée" : STATUS_FR[sub.status] || sub.status}
              </span>
            )}
            {!hasSub && data?.plan_source === "purchase" && (
              <span className="px-3 py-1.5 rounded-lg border-2 border-slate-900 font-bold text-sm bg-emerald-300">
                Licence — Paiement unique
              </span>
            )}
          </div>

          {hasSub && (
            <div className="mt-5 pt-5 border-t-2 border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <span className="text-sm font-semibold text-slate-700">
                {sub.cancel_at_period_end
                  ? `Votre accès reste actif jusqu'au ${fmtDate(sub.current_period_end)}, puis l'abonnement s'arrêtera.`
                  : `Prochain renouvellement le ${fmtDate(sub.current_period_end)}.`}
              </span>
              {data?.is_admin && (
                <button
                  data-testid="billing-portal-btn"
                  onClick={openPortal}
                  className="flex items-center gap-2 bg-white font-bold px-4 py-2.5 rounded-xl nb-btn text-sm"
                >
                  <CreditCard size={16} strokeWidth={2.5} /> Gérer mon paiement
                </button>
              )}
            </div>
          )}
        </div>

        {/* Cancel */}
        {data?.is_admin && hasSub && !sub.cancel_at_period_end && (
          <div className="nb-card p-6 mb-6 bg-red-50" data-testid="billing-cancel-card">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={22} />
              <div className="flex-1">
                <p className="font-heading font-bold text-lg">Résilier l'abonnement</p>
                <p className="text-slate-600 text-sm mt-1">
                  La résiliation prendra effet à la fin de la période en cours. Vous gardez l'accès jusque-là.
                </p>
                {!confirm ? (
                  <button
                    data-testid="billing-cancel-btn"
                    onClick={() => setConfirm(true)}
                    className="mt-4 bg-white nb-btn font-bold px-5 py-2.5 rounded-xl text-red-600"
                  >
                    Résilier
                  </button>
                ) : (
                  <div className="mt-4 flex items-center gap-3">
                    <button
                      data-testid="billing-cancel-confirm"
                      onClick={cancel}
                      disabled={canceling}
                      className="bg-red-500 text-white font-bold px-5 py-2.5 rounded-xl border-2 border-slate-900 disabled:opacity-60"
                    >
                      {canceling ? "Résiliation…" : "Confirmer la résiliation"}
                    </button>
                    <button onClick={() => setConfirm(false)} className="font-bold px-4 py-2.5 text-slate-600">
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invoices */}
        <div className="nb-card p-6" data-testid="billing-invoices">
          <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
            <FileText size={20} className="text-indigo-600" /> Factures
          </h2>
          {(!data?.invoices || data.invoices.length === 0) ? (
            <p className="text-slate-400 font-medium py-4" data-testid="billing-no-invoices">
              Aucune facture disponible pour le moment.
            </p>
          ) : (
            <div className="divide-y-2 divide-slate-100">
              {data.invoices.map((inv, i) => (
                <div key={inv.number || i} className="flex items-center justify-between py-3" data-testid={`invoice-row-${i}`}>
                  <div>
                    <p className="font-bold">{inv.number || "Facture"}</p>
                    <p className="text-slate-500 text-sm font-medium">{fmtDate(inv.created)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-heading font-black">{inv.amount.toFixed(2)} {inv.currency}</span>
                    <span className={`text-xs font-bold px-2 py-1 rounded ${inv.status === "paid" ? "bg-emerald-200" : "bg-slate-200"}`}>
                      {inv.status === "paid" ? "Payée" : inv.status}
                    </span>
                    {inv.hosted_invoice_url && (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 font-bold text-sm inline-flex items-center gap-1 hover:underline"
                      >
                        Voir <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
